import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { faFilter, faGear } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { call } from '@spacefill/shared/src/utils/Backend';
import { ButtonText } from '@spacefill/uikit/src/components/ButtonText/ButtonText';
import { ChipNumber } from '@spacefill/uikit/src/components/ChipNumber';
import { Dropdown } from '@spacefill/uikit/src/components/Dropdown';
import { SidePanel } from '@spacefill/uikit/src/components/SidePanel';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import { InAppNotificationsList } from './InAppNotificationsList';

const PANEL_CHIP_CAP = 99;

function formatCount(value, cap) {
    if (value <= 0 || Number.isNaN(value)) return '';
    return value > cap ? `${cap}+` : String(value);
}

export function NotificationsSidePanel({ isOpen, onClose, unreadCount = 0 }) {
    const { t } = useTranslation();
    const [filter, setFilter] = useState('all');
    const queryClient = useQueryClient();

    const { mutate: readAllMutate } = useMutation({
        mutationFn: () => call('/in-app-notifications/read-all', {}, 'POST'),
        async onSuccess() {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['/in-app-notifications/list'] }),
                queryClient.invalidateQueries({ queryKey: ['/in-app-notifications/unread'] }),
            ]);
        },
        onError(error) {
            console.error('error during readAll mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const filterOptions = [
        { value: 'all', content: t('All'), onClick: () => setFilter('all') },
        { value: 'unread', content: t('Unread'), onClick: () => setFilter('unread') },
        { value: 'read', content: t('Read'), onClick: () => setFilter('read') },
        { value: 'read-all', content: t('Mark all as read'), onClick: () => readAllMutate(), divider: 'top' },
    ];

    const title = (
        <div className='flex items-center gap-2'>
            <span>{t('Notifications')}</span>
            {unreadCount > 0 && (
                <ChipNumber
                    variant='red'
                    value={formatCount(unreadCount, PANEL_CHIP_CAP)}
                />
            )}
        </div>
    );

    const titleSideElement = (
        <div className='flex items-center gap-2'>
            <ButtonText
                type='link'
                to='/profile/'
                onClick={onClose}
            >
                <FontAwesomeIcon icon={faGear} /> {t('Settings')}
            </ButtonText>
            <Dropdown
                size='small'
                inPortal={false}
                placement='bottom-end'
                iconEnd={
                    <FontAwesomeIcon
                        icon={faFilter}
                        className='h-4 text-gray-500'
                    />
                }
                options={filterOptions}
            />
        </div>
    );

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            titleSideElement={titleSideElement}
            width={500}
        >
            <InAppNotificationsList
                unreadCount={unreadCount}
                filter={filter}
            />
        </SidePanel>
    );
}
